/**
 * db.js — IndexedDB (via Dexie) schema for the offline-dispensing PWA.
 *
 *   meta       { key, value }                          one-row settings (snapshot_at, pharmacy_name)
 *   user       { id=1, …, token, token_exp }           the single logged-in user (or absent)
 *   drugs      { drug_id, brand_name, generic_name, … } the cached catalogue
 *   outbox     { client_op_id, status, payload, … }    pending POS submissions
 *   receipts   { client_op_id, pdf_blob, occurred_at } generated PDFs (for re-share)
 *   favourites { drug_id, added_at }                   per-device pinned drugs (one-tap add)
 *   held       { id, created_at, lines, … }            parked carts (resume later)
 *
 * Why one DB rather than per-pharmacy: a phone serves a single pharmacy at
 * once; switching pharmacies = sign out + sign in (which clears the cache).
 * Multi-pharmacy is a v1.1 problem (see DESIGN_PWA.md §11).
 */
import Dexie from 'dexie'

export const db = new Dexie('pharmacare-pwa')

db.version(1).stores({
  meta:     'key',
  user:     'id',
  drugs:    'drug_id, brand_name, generic_name',
  outbox:   'client_op_id, status, created_at',
  receipts: 'client_op_id, occurred_at',
})

// v2 — per-device "favourites": cashiers pin the drugs they sell most so they
// can add them with a single tap from the Browse panel. Local only (not
// synced): a phone's favourites reflect how that one cashier works. Existing
// tables carry over untouched; Dexie just adds the new `favourites` store.
db.version(2).stores({
  favourites: 'drug_id, added_at',
})

// v3 — "hold / park sale": a cashier parks a cart (customer stepped away,
// waiting on cash) and rings up someone else, then resumes it. Persisted so it
// survives an app restart or the phone going to sleep mid-queue.
db.version(3).stores({
  held: 'id, created_at',
})

// ── Tiny convenience helpers ────────────────────────────────────────────────

export async function getMeta(key) {
  const row = await db.meta.get(key)
  return row?.value ?? null
}

export async function setMeta(key, value) {
  await db.meta.put({ key, value })
}

export async function getUser() {
  return db.user.get(1)
}

export async function setUser(user) {
  await db.user.put({ id: 1, ...user })
}

export async function clearUser() {
  await db.user.clear()
}

export async function replaceDrugs(drugs) {
  await db.transaction('rw', db.drugs, async () => {
    await db.drugs.clear()
    if (Array.isArray(drugs) && drugs.length) await db.drugs.bulkPut(drugs)
  })
}

export async function getAllDrugs() {
  return db.drugs.toArray()
}

// Local stock decrement after a successful in-PWA sale. Cosmetic — the
// authoritative ledger lives on the desktop — but it stops the next cart
// from seeing stock the previous one just sold.
export async function decrementLocalStock(drugId, qty) {
  const row = await db.drugs.get(drugId)
  if (!row) return
  const next = Math.max(0, (Number(row.total_stock) || 0) - qty)
  await db.drugs.update(drugId, { total_stock: next })
}

// ── Favourites ──────────────────────────────────────────────────────────────
// Pinned drugs for one-tap add. Stored as { drug_id, added_at } and cross-
// referenced against the cached `drugs` table at render time, so a favourite
// that's been deleted from the catalogue simply stops showing up.

export async function getFavouriteIds() {
  const rows = await db.favourites.toArray()
  return new Set(rows.map(r => r.drug_id))
}

/** Toggle a drug's pinned state. Returns the new state (true = now pinned). */
export async function toggleFavourite(drugId) {
  const existing = await db.favourites.get(drugId)
  if (existing) {
    await db.favourites.delete(drugId)
    return false
  }
  await db.favourites.put({ drug_id: drugId, added_at: Date.now() })
  return true
}

// ── Held / parked sales ─────────────────────────────────────────────────────
// A cashier can park a cart and ring up someone else, then resume it. Each
// record stores the cart lines plus the in-progress discount; prices and stock
// are re-hydrated from the latest catalogue when the sale is resumed.

export async function listHeld() {
  return db.held.orderBy('created_at').reverse().toArray()
}

export async function putHeld(sale) {
  await db.held.put(sale)
}

export async function deleteHeld(id) {
  await db.held.delete(id)
}

export async function countHeld() {
  return db.held.count()
}

// ── Receipts history ────────────────────────────────────────────────────────

/**
 * All receipts on this device, newest first. Joined with the outbox row so
 * the caller knows whether each sale has been confirmed on the server
 * (SYNCED), is still in flight (QUEUED), or failed (REJECTED).
 */
export async function listReceipts() {
  const receipts = await db.receipts.orderBy('occurred_at').reverse().toArray()
  const outbox = await db.outbox.toArray()
  const byId = new Map(outbox.map(r => [r.client_op_id, r]))
  return receipts.map(r => ({
    ...r,
    outbox_status: byId.get(r.client_op_id)?.status || 'UNKNOWN',
  }))
}

export async function getReceipt(clientOpId) {
  return db.receipts.get(clientOpId)
}

export async function getDrugsCount() {
  return db.drugs.count()
}
